const FORBIDDEN_TAGS = new Set([
    'script',
    'style',
    'meta',
    'link',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'button',
    'textarea',
    'select',
    'option',
    'video',
    'audio',
    'canvas',
    'svg',
    'base',
    'noscript',
    'template'
]);

const URL_ATTRIBUTES = new Set(['href', 'src']);
const UNSAFE_URL_PATTERN = /^\s*(javascript|vbscript|data):/i;
const EVENT_HANDLER_PATTERN = /^on/i;
const MSO_STYLE_PATTERN = /(?:^|;)\s*mso-[^:;]+:[^;]*(;|$)/gi;
const CONDITIONAL_COMMENT_PATTERN = /<!--\[if[\s\S]*?<!\[endif\]-->/gi;
const WORD_NAMESPACE_TAG_PATTERN = /^[ovwx]:/i;

const isUnsafeUrl = (value: string): boolean => UNSAFE_URL_PATTERN.test(value);

const cleanInlineStyle = (value: string): string => value.replace(MSO_STYLE_PATTERN, ';').replace(/;;+/g, ';').replace(/^;|;$/g, '').trim();

const sanitizeAttributes = (element: Element): void => {
    Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value;

        if (EVENT_HANDLER_PATTERN.test(name) || (name === 'style' && !value.trim())) {
            element.removeAttribute(attribute.name);
            return;
        }

        if (name === 'style') {
            const cleaned = cleanInlineStyle(value);
            if (!cleaned) {
                element.removeAttribute('style');
            } else if (cleaned !== value) {
                element.setAttribute('style', cleaned);
            }
            return;
        }

        if (URL_ATTRIBUTES.has(name) && isUnsafeUrl(value)) {
            element.removeAttribute(attribute.name);
        }
    });
};

const unwrapElement = (element: Element): void => {
    const parent = element.parentNode;
    if (!parent) {
        element.remove();
        return;
    }

    while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
    }
    parent.removeChild(element);
};

const sanitizeNode = (node: Node): void => {
    if (node.nodeType === Node.COMMENT_NODE) {
        node.parentNode?.removeChild(node);
        return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    if (FORBIDDEN_TAGS.has(tagName) || WORD_NAMESPACE_TAG_PATTERN.test(tagName)) {
        if (FORBIDDEN_TAGS.has(tagName)) {
            element.remove();
        } else {
            Array.from(element.childNodes).forEach(sanitizeNode);
            unwrapElement(element);
        }
        return;
    }

    sanitizeAttributes(element);
    Array.from(element.childNodes).forEach(sanitizeNode);
};

const stripWordJunk = (html: string): string => html.replace(CONDITIONAL_COMMENT_PATTERN, '');

export const sanitizePastedHtml = (html: string): string => {
    const trimmed = html.trim();
    if (!trimmed) return '';

    const cleaned = stripWordJunk(trimmed);
    const document = new DOMParser().parseFromString(`<body>${cleaned}</body>`, 'text/html');
    const body = document.body;

    Array.from(body.childNodes).forEach(sanitizeNode);

    return body.innerHTML.trim();
};

export type PastePayload = { html: string; text?: never } | { html?: never; text: string } | { html: null; text: null };

export const resolvePastePayload = (html: string | null | undefined, plainText: string | null | undefined): PastePayload => {
    const sanitizedHtml = html ? sanitizePastedHtml(html) : '';
    if (sanitizedHtml) return { html: sanitizedHtml };

    const text = plainText?.trim() ? plainText : null;
    if (text) return { text };

    return { html: null, text: null };
};

export const insertSanitizedHtml = (document: Document, html: string): void => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const fragment = template.content;
    if (!fragment.childNodes.length) return;

    const selection = document.getSelection();
    if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const lastNode = fragment.lastChild;
        range.insertNode(fragment);

        if (lastNode) {
            const nextRange = document.createRange();
            nextRange.setStartAfter(lastNode);
            nextRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(nextRange);
        }
        return;
    }

    document.body?.appendChild(fragment);
};
